/**
 * KineticReview — Coach review queue for kinetic benchmark sessions.
 *
 * Shows sealed sessions from the coach's locker room athletes.
 * Coach can approve (writes to leaderboard + triggers flair) or reject (with reason).
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/auth-context';
import ReviewCard from '@/components/kinetic/ReviewCard';

const KTS_URL = import.meta.env.VITE_KTS_URL || '';

type FilterStatus = 'pending' | 'approved' | 'rejected';

export default function KineticReview() {
  const { session } = useAuth();
  const token = session?.access_token || '';

  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterStatus>('pending');

  const loadQueue = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${KTS_URL}/api/review/queue?status=${filter}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Failed: ${res.status}`);
      }

      const { data } = await res.json();
      setSessions(data || []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const handleApproved = (sessionId: string) => {
    setSessions((prev) => prev.filter((s) => s.id !== sessionId));
  };

  const handleRejected = (sessionId: string) => {
    setSessions((prev) => prev.filter((s) => s.id !== sessionId));
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Kinetic Review</h1>
        <p className="mt-1 text-sm text-gray-500">
          Review and verify athlete benchmark submissions from your locker room.
        </p>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 rounded-xl bg-gray-100 p-1">
        {(['pending', 'approved', 'rejected'] as FilterStatus[]).map((status) => (
          <button
            key={status}
            type="button"
            onClick={() => setFilter(status)}
            className={`flex-1 rounded-lg py-2 text-sm font-medium transition-colors ${
              filter === status
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
            {status === 'pending' && sessions.length > 0 && filter === 'pending' && (
              <span className="ml-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white">
                {sessions.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-xl bg-red-50 p-4 text-center">
          <p className="text-sm text-red-600">{error}</p>
          <button
            type="button"
            onClick={loadQueue}
            className="mt-2 text-xs font-medium text-red-700 underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Queue */}
      {!loading && !error && (
        <div className="space-y-3">
          {sessions.length === 0 && (
            <div className="rounded-2xl bg-gray-50 py-12 text-center">
              <p className="text-lg font-semibold text-gray-400">
                {filter === 'pending' ? 'No pending reviews' : `No ${filter} sessions`}
              </p>
              <p className="mt-1 text-sm text-gray-400">
                {filter === 'pending'
                  ? 'When your athletes complete benchmarks, they\u2019ll appear here.'
                  : 'Switch to Pending to see active reviews.'}
              </p>
            </div>
          )}

          {sessions.map((s) => (
            <ReviewCard
              key={s.id}
              session={s}
              onApproved={handleApproved}
              onRejected={handleRejected}
              ktsUrl={KTS_URL}
              token={token}
            />
          ))}
        </div>
      )}
    </div>
  );
}
